import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { XMarkIcon, PencilIcon, ArrowDownTrayIcon, CheckIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { companySettingsService } from '../services/system/company-settings';
import { clientsService } from '../services/logistics/clients';
import { invoicePDFService } from '../services/pdf/invoice-pdf';
import AuditHistoryButton from '../components/audit/AuditHistoryButton';
import { AUDIT_MODULES } from '../services/system/audit';

const InvoiceDetailModal = ({ invoice, onClose, onUpdate }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [invoiceData, setInvoiceData] = useState(null);
  const [company, setCompany] = useState(null);
  const [client, setClient] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState({});
  const [editingItems, setEditingItems] = useState([]);
  const { currentCompany } = useAuth();

  useEffect(() => {
    if (invoice && currentCompany?.id) {
    const loadData = async () => {
        try {
      setLoading(true);
          
          console.log('🔍 Loading invoice data...');
          console.log('📋 Invoice object:', invoice);
          console.log('👤 Current company ID:', currentCompany?.id);
          console.log('🏢 Client ID:', invoice?.clientId);

          // Load invoice data
        setInvoiceData(invoice);
        setForm({
            issueDate: invoice.issueDate || new Date().toISOString().split('T')[0],
            dueDate: invoice.dueDate || new Date().toISOString().split('T')[0],
            description: invoice.description || '',
          vatId: invoice.vatId || ''
        });
        setEditingItems(invoice.items || []);
        
          // Load company profile data from Settings
          console.log('🏢 Loading company profile data for companyId:', currentCompany?.id);
          
          try {
            const companyProfileData = await companySettingsService.getCompanySettings(currentCompany?.id);
            console.log('✅ Company profile data loaded:', companyProfileData);
            
            if (companyProfileData) {
              setCompany(companyProfileData);
          } else {
              console.log('❌ No company profile data found in Settings');
              // Fallback to empty company data
              setCompany({
                companyName: currentCompany?.name || 'Company Name Not Set',
                address: 'Address Not Set',
                phone: 'Phone Not Set',
                email: 'Email Not Set',
                vatId: 'VAT ID Not Set',
                bankName: 'Bank Name Not Set',
                iban: 'IBAN Not Set',
                accountName: 'Account Name Not Set'
              });
            }
          } catch (error) {
            console.error('❌ Error loading company profile:', error);
            // Fallback to empty company data
            setCompany({
              companyName: currentCompany?.name || 'Company Name Not Set',
              address: 'Address Not Set',
              phone: 'Phone Not Set',
              email: 'Email Not Set',
              vatId: 'VAT ID Not Set',
              bankName: 'Bank Name Not Set',
              iban: 'IBAN Not Set',
              accountName: 'Account Name Not Set'
            });
          }

          // Load client data from clients collection
          if (invoice.clientId) {
            console.log('👤 Loading client data for clientId:', invoice.clientId);
        try {
          const clientData = await clientsService.getClient(invoice.clientId, currentCompany?.id);
              console.log('✅ Client data loaded:', clientData);
              setClient(clientData);
            } catch (error) {
              console.error('❌ Error loading client data:', error);
              setClient({
                name: 'Client Not Found',
                companyName: 'Client Not Found',
                address: 'Address Not Available',
                phone: 'Phone Not Available',
                email: 'Email Not Available'
              });
            }
          } else {
            console.log('⚠️ No clientId provided in invoice');
            setClient({
              name: 'No Client Selected',
              companyName: 'No Client Selected',
              address: 'No Address',
              phone: 'No Phone',
              email: 'No Email'
            });
        }
      } catch (error) {
          console.error('❌ Error loading data:', error);
          console.error('�� Current company ID:', currentCompany?.id);
          console.error('🏢 Invoice clientId:', invoice?.clientId);
          console.error('📋 Full invoice object:', invoice);
          console.error('🔥 Firebase error details:', error.code, error.message);
          toast.error('Error loading invoice data: ' + error.message);
      } finally {
        setLoading(false);
      }
    };

      loadData();
    } else {
      console.log('⚠️ Cannot load data - missing invoice or current company');
      console.log('📋 Invoice exists:', !!invoice);
      console.log('👤 Current company exists:', !!currentCompany?.id);
      console.log('🔍 Current company object:', currentCompany);
    }
  }, [invoice, currentCompany]);

  const handleEdit = () => setEditMode(true);
  const handleCancelEdit = () => {
    setEditMode(false);
    setForm({
      issueDate: invoiceData.issueDate || new Date().toISOString().split('T')[0],
      dueDate: invoiceData.dueDate || new Date().toISOString().split('T')[0],
      description: invoiceData.description || '',
      vatId: invoiceData.vatId || ''
    });
    setEditingItems(invoiceData.items || []);
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...editingItems];
    updatedItems[index] = { ...updatedItems[index], [field]: value };
    setEditingItems(updatedItems);
  };

  const handleSave = async () => {
    try {
      // Update invoice with new data
      const updatedInvoice = {
        ...invoiceData,
        ...form,
        items: editingItems
      };

      await updateDoc(doc(db, 'companies', currentCompany?.id, 'invoices', invoice.id), updatedInvoice);
      
      setInvoiceData(updatedInvoice);
      setEditMode(false);
      toast.success('Invoice updated successfully');
      
      if (onUpdate) {
        onUpdate(updatedInvoice);
      }
    } catch (error) {
      console.error('Error updating invoice:', error);
      toast.error('Error updating invoice');
    }
  };

  const generatePDF = async () => {
    try {
      console.log('🔄 Generating enhanced invoice PDF...');
      
      if (!invoiceData || !currentCompany?.id) {
        toast.error('Missing required data for PDF generation');
        return;
      }

      // Use the enhanced PDF service
      await invoicePDFService.generateInvoicePDF(
        invoiceData.id, 
        currentCompany.id, 
        'en' // You can make this dynamic based on user preference
      );
      
      console.log('✅ Enhanced PDF generated successfully');
      toast.success('Invoice PDF downloaded successfully');
      
    } catch (error) {
      console.error('❌ Error generating enhanced PDF:', error);
      toast.error(`Error generating PDF: ${error.message}`);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl p-8">
          <span>{t('common.loading', t('common.loading'))}</span>
        </div>
      </div>
    );
  }

  if (!invoiceData) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] overflow-y-auto m-4 p-6 relative">
        <button 
          onClick={onClose} 
          className="absolute top-3 right-3 btn btn-sm btn-circle btn-ghost hover:bg-gray-200 z-10"
          title={t('common.close')}
        >
          <XMarkIcon className="w-5 h-5" />
        </button>
        
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold">{t('invoices.invoice_details', 'Invoice Details')}</h2>
          <div className="flex space-x-2 items-center">
            {invoice?.id && (
              <AuditHistoryButton module={AUDIT_MODULES.INVOICE} recordId={invoice.docId || invoice.id} />
            )}
            <button
              onClick={generatePDF}
              className="btn btn-outline"
            >
              <ArrowDownTrayIcon className="w-4 h-4 mr-1" />
              {t('invoices.download_pdf', 'Download PDF')}
            </button>
            {!editMode && (
              <button onClick={handleEdit} className="btn btn-outline">
                <PencilIcon className="w-4 h-4 mr-1" /> {t('common.edit', t('common.edit'))}
              </button>
            )}
          </div>
        </div>

        {/* Invoice Header */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('accounting.table.invoice_id')}
              </label>
              <div className="font-mono text-lg font-bold">{invoiceData.id}</div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('invoices.issue_date', 'Issue Date')}
                </label>
                {editMode ? (
                  <input 
                    type="date" 
                    name="issueDate" 
                    value={form.issueDate} 
                    onChange={handleChange} 
                    className="input input-bordered w-full" 
                  />
                ) : (
                  <div>{invoiceData.issueDate}</div>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('invoices.due_date', 'Due Date')}
                </label>
                {editMode ? (
                  <input 
                    type="date" 
                    name="dueDate" 
                    value={form.dueDate} 
                    onChange={handleChange} 
                    className="input input-bordered w-full" 
                  />
                ) : (
                  <div>{invoiceData.dueDate}</div>
                )}
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Company Information
              </label>
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center space-x-3">
                  {company?.logoUrl && (
                    <img src={company.logoUrl} alt="Logo" className="h-12 w-12 object-contain" />
                  )}
                  <div>
                    <div className="font-semibold">{company?.companyName}</div>
                    <div className="text-sm text-gray-600">{company?.address}</div>
                    {company?.phone && <div className="text-sm text-gray-600">{company.phone}</div>}
                    {company?.website && <div className="text-sm text-gray-600">{company.website}</div>}
                  </div>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('invoices.client', 'Client')}
              </label>
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="font-semibold">{client?.name || client?.companyName}</div>
                {client?.address && <div className="text-sm text-gray-600">{client.address}</div>}
                {client?.phone && <div className="text-sm text-gray-600">{client.phone}</div>}
                {client?.email && <div className="text-sm text-gray-600">{client.email}</div>}
              </div>
            </div>
          </div>
        </div>

        {/* Items Table */}
        <div className="mb-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Invoice Items</h3>
          </div>
          
          <div className="overflow-x-auto">
            <table className="table w-full">
              <thead>
                <tr>
                  <th className="w-12">#</th>
                  <th>Description</th>
                  <th>Details</th>
                  <th className="w-20">Qty</th>
                  <th className="w-24">Unit Price</th>
                  <th className="w-24">Total</th>
                </tr>
              </thead>
              <tbody>
                {(editMode ? editingItems : invoiceData.items || []).map((item, idx) => (
                  <React.Fragment key={idx}>
                    {/* Main shipment item */}
                    <tr className="bg-white">
                      <td className="font-semibold">{idx + 1}</td>
                    <td>
                      {editMode ? (
                        <input
                          type="text"
                          value={item.description || ''}
                          onChange={(e) => handleItemChange(idx, 'description', e.target.value)}
                          className="input input-bordered input-sm w-full"
                        />
                      ) : (
                          <div className="font-medium">{item.description}</div>
                      )}
                    </td>
                    <td>
                      {editMode ? (
                        <input
                          type="text"
                          value={item.comments || ''}
                          onChange={(e) => handleItemChange(idx, 'comments', e.target.value)}
                          className="input input-bordered input-sm w-full"
                          placeholder="Optional comments"
                        />
                      ) : (
                          <span className="text-gray-500 text-sm">{item.comments || '-'}</span>
                      )}
                    </td>
                    <td>
                      {editMode ? (
                        <input
                          type="number"
                          value={item.quantity || ''}
                            onChange={(e) => handleItemChange(idx, 'quantity', Number(e.target.value))}
                          className="input input-bordered input-sm w-full"
                          min="0"
                        />
                      ) : (
                          <span>{item.quantity || 0}</span>
                      )}
                    </td>
                    <td>
                      {editMode ? (
                        <input
                          type="number"
                          value={item.unitPrice || ''}
                            onChange={(e) => handleItemChange(idx, 'unitPrice', Number(e.target.value))}
                          className="input input-bordered input-sm w-full"
                          min="0"
                          step="0.01"
                        />
                      ) : (
                          <span>{(Number(item.unitPrice) || 0).toFixed(2)} SAR</span>
                      )}
                    </td>
                      <td>
                      <span className="font-semibold">
                        {((Number(item.unitPrice) || 0) * (Number(item.quantity) || 1) + 
                          ((item.additionalCosts || []).reduce((sum, cost) => sum + ((Number(cost.quantity) || 1) * (Number(cost.unitPrice) || 0)), 0))
                        ).toFixed(2)} SAR
                      </span>
                      </td>
                    </tr>
                    
                    {item.additionalCosts && item.additionalCosts.length > 0 && item.additionalCosts.map((cost, costIdx) => (
                      <tr key={`${idx}-${costIdx}`} className="bg-gray-50">
                        <td></td>
                        <td>
                          <div className="flex items-center">
                            <span className="text-gray-400 mr-2">↳</span>
                            <span className="text-sm">{cost.description}</span>
                          </div>
                        </td>
                        <td>
                          <span className="text-xs text-gray-500">{cost.type}</span>
                        </td>
                        <td>
                          <span className="text-sm">{cost.quantity || 0}</span>
                        </td>
                        <td>
                          <span className="text-sm">{(Number(cost.unitPrice) || 0).toFixed(2)} SAR</span>
                        </td>
                        <td>
                          <span className="text-sm">{((Number(cost.quantity) || 0) * (Number(cost.unitPrice) || 0)).toFixed(2)} SAR</span>
                    </td>
                  </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Invoice Totals Section */}
        {(() => {
          // Calculate totals
          const subtotal = (invoiceData.items || []).reduce((sum, item) => {
            const itemTotal = (item.unitPrice || 0) * (item.quantity || 1);
            const additionalTotal = (item.additionalCosts || []).reduce((accSum, cost) => {
              return accSum + (cost.total || ((cost.quantity || 1) * (cost.unitPrice || 0)));
                  }, 0);
            return sum + itemTotal + additionalTotal;
                  }, 0);

          const vatRate = 0.15; // 15% VAT
          const vatAmount = subtotal * vatRate;
          const total = subtotal + vatAmount;

          return (
            <div className="mb-6">
              <div className="flex justify-end">
                <div className="w-full max-w-md">
                  <div className="bg-gray-50 p-6 rounded-lg border">
                    <h3 className="text-lg font-semibold mb-4">Invoice Summary</h3>
                    
                    <div className="space-y-3">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Subtotal:</span>
                        <span className="font-medium">SAR {subtotal.toLocaleString()}</span>
              </div>
                      
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">VAT ({(vatRate * 100).toFixed(0)}%):</span>
                        <span className="font-medium">SAR {vatAmount.toLocaleString()}</span>
              </div>
                      
                      <div className="border-t border-gray-300 pt-3">
                        <div className="flex justify-between text-lg font-bold">
                          <span className="text-gray-900">Total:</span>
                          <span className="text-blue-600">SAR {total.toLocaleString()}</span>
            </div>
          </div>
        </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Edit Mode Actions */}
        {editMode && (
          <div className="flex justify-end space-x-2 mt-6">
            <button onClick={handleCancelEdit} className="btn btn-outline">
              {t('common.cancel', t('common.cancel'))}
            </button>
            <button onClick={handleSave} className="btn btn-primary">
              <CheckIcon className="w-4 h-4 mr-1" /> {t('common.save', t('common.save'))}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default InvoiceDetailModal; 